import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { CalibrationPendencyItem } from "@/types/closed-loop.types";
import { AwaitingClassificationList } from "./AwaitingClassificationList";
import { PendencyFilters } from "./PendencyFilters";
import { PendencyList } from "./PendencyList";
import { QueuePager } from "./QueuePager";
import { ResolvePendencyDialog } from "./ResolvePendencyDialog";
import { TrackRecordTab } from "./track-record/TrackRecordTab";
import { useAwaitingClassification, usePendencies, type PendencyFilterState } from "./hooks";

// Spec 077 (US2) — o CICLO FECHADO: as duas filas de trabalho que a apuração produz.
//
// Orquestrador puro (hooks + composição); tudo que desenha mora ao lado.
//
// As abas são nomeadas pelo TRABALHO, não pela entidade (FR-036), e cada uma leva a contagem do
// SERVIDOR (FR-037) — nunca `items.length`, que mudaria ao paginar e destruiria a única coisa que
// esta tela promete de relance.

export function ClosedLoopPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<PendencyFilterState>({ status: "OPEN" });
  const [resolving, setResolving] = useState<CalibrationPendencyItem | null>(null);
  // Um offset por fila: as duas paginam de forma independente, e trocar de aba não deve rebobinar
  // a outra. Trocar de FILTRO, sim — a página 3 de uma filtragem não descreve nada na seguinte.
  const [awaitingOffset, setAwaitingOffset] = useState(0);
  const [pendencyOffset, setPendencyOffset] = useState(0);
  // `Tabs` CONTROLADA, e não `defaultValue`: `TabsContent` renderiza os filhos sempre e só aplica
  // `hidden`, então a régua — a consulta mais cara da tela — dispararia em toda visita a esta
  // página, para uma aba que a maioria não abre. E é o que permite ao estado vazio da régua mandar
  // o gestor para a fila certa em vez de para fora da tela.
  const [tab, setTab] = useState("awaiting");

  const awaiting = useAwaitingClassification(undefined, awaitingOffset);
  const pendencies = usePendencies(filters, pendencyOffset);

  function changeFilters(next: PendencyFilterState) {
    setFilters(next);
    setPendencyOffset(0);
  }

  return (
    <section>
      <PageHeader
        title="Ciclo fechado"
        subtitle="O que já foi apurado e espera um julgamento, e o trabalho de calibração que os julgamentos geraram."
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList aria-label="Filas do ciclo fechado">
          <TabsTrigger value="awaiting">
            Aguardando classificação{awaiting.data ? ` (${awaiting.data.total})` : ""}
          </TabsTrigger>
          <TabsTrigger value="pendencies">
            Ações de calibração{pendencies.data ? ` (${pendencies.data.total})` : ""}
          </TabsTrigger>
          <TabsTrigger value="track-record">Régua do gêmeo</TabsTrigger>
        </TabsList>

        <TabsContent value="awaiting" className="pt-4">
          {/* ⚠️ `isError` ANTES de decidir entre vazio e com dados (FR-042). Uma fila que falhou
              renderizada como vazia diz "não há trabalho" quando a verdade é "não sei". */}
          {awaiting.isError ? (
            <Notify.QueryError
              error={awaiting.error}
              onRetry={() => void awaiting.refetch()}
              title="Não foi possível carregar as apurações aguardando classificação"
            />
          ) : awaiting.isPending ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : awaiting.data.items.length > 0 ? (
            <div className="space-y-2">
              <AwaitingClassificationList items={awaiting.data.items} />
              <QueuePager
                total={awaiting.data.total}
                offset={awaitingOffset}
                onOffsetChange={setAwaitingOffset}
              />
            </div>
          ) : (
            <Notify.Empty
              icon={<ClipboardCheck className="h-5 w-5" />}
              title="Nenhuma apuração esperando julgamento"
              description="Assim que um compromisso for apurado, ele aparece aqui com a causa já proposta e a evidência ao lado — basta confirmar ou discordar."
              action={
                <Button variant="outline" onClick={() => navigate("/simulations")}>
                  Ir para as simulações
                </Button>
              }
            />
          )}
        </TabsContent>

        <TabsContent value="pendencies" className="space-y-4 pt-4">
          <PendencyFilters value={filters} onChange={changeFilters} />
          {pendencies.isError ? (
            <Notify.QueryError
              error={pendencies.error}
              onRetry={() => void pendencies.refetch()}
              title="Não foi possível carregar as ações de calibração"
            />
          ) : pendencies.isPending ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : pendencies.data.items.length > 0 ? (
            <div className="space-y-2">
              <PendencyList items={pendencies.data.items} onResolve={setResolving} />
              <QueuePager
                total={pendencies.data.total}
                offset={pendencyOffset}
                onOffsetChange={setPendencyOffset}
              />
            </div>
          ) : (
            <Notify.Empty
              icon={<Wrench className="h-5 w-5" />}
              title="Nenhuma ação de calibração nesta situação"
              description="Trabalho aparece aqui quando alguém classifica uma divergência como evento não modelado, premissa incorreta ou erro do motor — as três causas que pedem uma mudança no gêmeo."
              action={
                <Button variant="outline" onClick={() => changeFilters({ status: "OPEN" })}>
                  Ver as abertas
                </Button>
              }
            />
          )}
        </TabsContent>

        {/* ⚠️ DELEGA, e não desenha: este arquivo é orquestrador puro, e todo o conteúdo da régua
            mora em `track-record/`, ao lado. Quem enforça o orçamento é o `max-lines` do ESLint —
            aritmética de LOC escrita à mão aqui envelheceria na próxima edição deste arquivo.

            O `tab === ...` monta a aba só quando ela está aberta: `TabsContent` renderiza os filhos
            sempre e só aplica `hidden`, e a régua é a consulta mais cara desta tela. */}
        <TabsContent value="track-record" className="pt-4">
          {tab === "track-record" ? (
            <TrackRecordTab onGoToAwaiting={() => setTab("awaiting")} />
          ) : null}
        </TabsContent>
      </Tabs>

      <ResolvePendencyDialog
        pendency={resolving}
        onOpenChange={(open) => !open && setResolving(null)}
      />
    </section>
  );
}
